import type { AppState } from "../../shared/types";
import { actionContext, previewActions } from "./optimistic";
import type { ApiResponse } from "./api";

interface Pending {
  id: string;
  actions: unknown[];
  context: string | null;
  compute: boolean;
  applied?: AppState;
  label?: string;
  conflicts?: number;
}
export interface ActionJournal {
  base: AppState;
  pending: Pending[];
}
export interface QueueStatus {
  pending: number;
  saving: boolean;
  error: string;
  computing: string | null;
}
export interface QueueTransport {
  send: (
    actions: unknown[],
    revision: number,
    id: string,
    signal: AbortSignal,
    applied: (state: AppState) => void,
  ) => Promise<ApiResponse>;
  load: () => Promise<AppState>;
  cancel: (id: string) => Promise<void>;
}
export class ActionQueue {
  private base: AppState;
  private view: AppState;
  private pending: Pending[] = [];
  private running = false;
  private rendering = Promise.resolve();
  private editing = Promise.resolve();
  private error = "";
  private hadJournal = false;
  private controller: AbortController | null = null;
  private listeners: (() => void)[] = [];
  constructor(
    initial: AppState,
    private transport: QueueTransport,
    private publish: (state: AppState) => void,
    private status: (status: QueueStatus) => void,
    private report: (message: string) => void,
    private journal: (data: ActionJournal) => void = () => {},
  ) {
    this.base = this.view = initial;
  }
  get snapshot() {
    return this.base;
  }
  get hasPending() {
    return this.pending.length > 0 || this.running;
  }
  private emit() {
    if (this.pending.length || this.hadJournal)
      this.journal({ base: this.base, pending: this.pending });
    this.hadJournal = this.pending.length > 0;
    this.status({
      pending: this.pending.length,
      saving: this.running,
      error: this.error,
      computing: this.pending[0]?.compute
        ? (this.pending[0].label ?? "Working…")
        : null,
    });
  }
  private render() {
    this.rendering = this.rendering.then(async () => {
      let view = structuredClone(this.base);
      for (const item of this.pending) {
        if (item.applied) {
          view = structuredClone(item.applied);
          continue;
        }
        try {
          view = (await previewActions(view, item.actions, item.id)) ?? view;
        } catch {
          /* The dispatcher rejects actions whose original context no longer exists. */
        }
      }
      this.view = view;
      this.publish(view);
      this.emit();
    });
    return this.rendering;
  }
  enqueue(actions: unknown[], label?: string) {
    const task = this.editing.then(() => this.add(actions, label));
    this.editing = task.catch(() => {});
    return task;
  }
  private async add(actions: unknown[], label?: string) {
    await this.rendering;
    const id = crypto.randomUUID();
    const context = actionContext(this.view, actions);
    const predicted = await previewActions(this.view, actions, id);
    // A new interaction supersedes an unfinished engine computation, but never
    // interrupts ordinary saves. Those retain their order and idempotency keys.
    if (this.pending[0]?.compute && !this.pending[0].applied)
      this.controller?.abort();
    this.pending.push({ id, actions, context, compute: !predicted, label });
    this.error = "";
    await this.render();
    void this.pump();
  }
  async replace(state: AppState, force = false) {
    if (force || state.revision >= this.base.revision) this.base = state;
    await this.render();
  }
  async restore(data: ActionJournal) {
    if (!data.pending.length) return;
    this.base = data.base;
    this.pending = data.pending;
    await this.render();
    void this.pump();
  }
  async retry() {
    this.error = "";
    for (const item of this.pending) item.conflicts = 0;
    await this.pump();
  }
  async flush() {
    await this.editing;
    await this.rendering;
    if (this.error) throw new Error(this.error);
    if (!this.hasPending) return;
    await new Promise<void>((resolve) => this.listeners.push(resolve));
    if (this.error) throw new Error(this.error);
  }
  cancelComputation() {
    if (this.pending[0]?.compute && !this.pending[0].applied)
      this.controller?.abort();
  }
  private async pump() {
    if (this.running || this.error) return;
    this.running = true;
    this.emit();
    try {
      while (this.pending.length && !this.error) {
        const item = this.pending[0];
        this.controller = new AbortController();
        if (
          item.context !== null &&
          actionContext(this.base, item.actions) !== item.context
        ) {
          this.pending.shift();
          this.report(
            "The position changed before that action could be saved. Please try it on the current board.",
          );
          await this.render();
          continue;
        }
        try {
          const result = await this.transport.send(
            item.actions,
            this.base.revision,
            item.id,
            this.controller.signal,
            (state) => {
              item.applied = state;
              void this.render();
            },
          );
          if (result.state.revision >= this.base.revision)
            this.base = result.state;
          this.pending.shift();
          await this.render();
        } catch (error) {
          const failure = error as Error & { status?: number };
          if (failure.name === "AbortError") {
            await this.transport.cancel(item.id);
            this.base = await this.transport.load();
            this.pending.shift();
            await this.render();
            continue;
          }
          if (failure.status === 409) {
            this.base = await this.transport.load();
            item.applied = undefined;
            item.conflicts = (item.conflicts ?? 0) + 1;
            if (item.conflicts >= 4) {
              this.error =
                "Another session is changing this workspace. Your changes are still here; retry saving when it settles.";
              this.report(this.error);
            }
            await this.render();
            continue;
          }
          if (
            failure.status &&
            failure.status >= 400 &&
            failure.status < 500 &&
            failure.status !== 429
          ) {
            this.pending.shift();
            this.report(failure.message);
            await this.render();
            continue;
          }
          // Keep visible local work and the same request ID. Retry can safely
          // recover a lost response even when the server already committed.
          this.error =
            "Your changes are still here, but haven’t finished saving. Reconnect and retry.";
          this.report(this.error);
          this.emit();
        }
      }
    } catch {
      this.error =
        "Your changes are still here. Retry saving when the connection returns.";
    } finally {
      this.running = false;
      this.controller = null;
      this.emit();
      for (const listener of this.listeners.splice(0)) listener();
    }
  }
}
